import {
  BusinessCollection,
  BusinessCollectionDetail,
  RawBusinessCollection,
  RawBusinessCollectionListResponse,
  RawBusinessCollectionMember,
  RawBusinessCollectionMemberDetail,
} from "../../../interfaces/BusinessCollectionObject";
import { BusinessProfile, RawBusiness } from "../../../interfaces/BusinessObject";
import { convertRawBusiness } from "./businessConvertor";

/** Members arrive either as bare ids or as (possibly sparse) business records. */
const memberId = (member: RawBusinessCollectionMember): string =>
  typeof member === "string" ? member : member?.businessId ?? "";

export function convertRawBusinessCollection(
  raw: RawBusinessCollection
): BusinessCollection {
  return {
    uid: raw.uid,
    creatorId: raw.creatorId ?? "",
    creatorName: raw.creatorName ?? null,
    name: raw.collectionName ?? "Untitled collection",
    businessIds: (raw.collection ?? []).map(memberId).filter(Boolean),
    bannerUrl: raw.bannerUrl ?? null,
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
  };
}

export function convertRawBusinessCollectionList(
  raw: RawBusinessCollectionListResponse
): BusinessCollection[] {
  // `contents` is null on a successful empty result — not an error.
  return (raw?.contents ?? []).map(convertRawBusinessCollection);
}

/**
 * A member is only renderable once it has a name; anything less is an id-only
 * stub from `includeBusinessDetails=false`.
 */
const isRenderableMember = (
  member: RawBusinessCollectionMember
): member is RawBusinessCollectionMemberDetail =>
  typeof member !== "string" && Boolean(member?.businessId) && Boolean(member?.name);

/**
 * Fills the fields `RawBusiness` declares non-nullable but a collection member
 * can omit, so the shared business converter never hands Redux a `BusinessProfile`
 * whose types lie about what's present.
 */
const toRawBusiness = (member: RawBusinessCollectionMemberDetail): RawBusiness => ({
  ...member,
  businessId: member.businessId,
  creatorId: member.creatorId ?? "",
  ownerId: member.ownerId ?? null,
  name: member.name ?? "",
  listingType: member.listingType ?? "",
  phone: member.phone ?? "",
});

/**
 * The collection endpoint's envelope: `contents` holds a single collection whose
 * `collection` array carries the member businesses.
 *
 * Members requested with `includeBusinessDetails=false` come back with every
 * field except businessId null, so those are dropped — a nameless card is not
 * worth rendering. Callers that need the grid must request the details.
 */
export function convertRawCollectionDetail(
  raw: RawBusinessCollectionListResponse
): BusinessCollectionDetail | null {
  const first = (raw?.contents ?? [])[0];
  if (!first) return null;

  const businesses: BusinessProfile[] = (first.collection ?? [])
    .filter(isRenderableMember)
    .map((member) => convertRawBusiness(toRawBusiness(member)));

  return { collection: convertRawBusinessCollection(first), businesses };
}

export default {
  convertRawBusinessCollection,
  convertRawBusinessCollectionList,
  convertRawCollectionDetail,
};
